import type { Address, CodeableConcept, Coding, Extension, HumanName } from "@medplum/fhirtypes";
import type { NullableExcept, SystemValuePair } from "@vibrant/models/fhir/fhir-resource-types";
import { parseName } from "humanparser";

export type NullableExtension = NullableExcept<Extension, "url">;

export type DefinedCoding = Coding & { code: string };

/**
 * Given an array of extensions, returns a new array containing only the extensions that have a value.
 *
 * Example:
 *
 * ```typescript
 * const extensions = [
 *   { url: "http://example.com/extension", valueBoolean: true },
 *   { url: "http://example.com/extension2" },
 * ];
 *
 * toFhirExtensions(extensions); // Only the first extension is in the resulting array.
 * ```
 *
 * @param extensions - The array of extensions to filter.
 * @returns The filtered array of extensions.
 */
export function toFhirExtensions(extensions: NullableExtension[]): NullableExtension[] | undefined {
  const filteredExtensions = extensions.filter((extension) => {
    if (!extension.url || extension.url.trim().length === 0) {
      return false;
    }

    let hasValue = false;
    for (const property in extension) {
      if (property === "url") continue;
      if (extension[property as keyof Extension] !== undefined) {
        hasValue = true;
        break;
      }
    }

    return hasValue;
  });

  if (filteredExtensions.length === 0) return undefined;

  return filteredExtensions;
}

/**
 * Removes empty values from a list of system-value pairs.
 * Issues a warning for entries missing a system.
 *
 * @param list - The list of system-value pairs.
 * @returns The list with invalid pairs removed.
 */
export function pruneSystemValuePairs(
  list: Array<Partial<SystemValuePair>>,
): Array<SystemValuePair> | undefined {
  const result = list.filter((item) => {
    if (item.system === undefined) {
      console.warn("pruneSystemValuePairs:missing system property:", item, "in", list);
    }
    if (item.value === undefined) return false;
    if (typeof item.value === "string" && item.value.trim().length === 0) return false;
    if (item.system === undefined || item.system.trim().length === 0) return false;
    return true;
  }) as Array<SystemValuePair>;

  if (result.length === 0) {
    return undefined;
  }

  return result;
}
/**
 * Creates a Coding object from a system-value pair.
 *
 * It returns undefined if there is no code.
 *
 * @param system - The identifier system URI or code.
 * @param code - The identifier code.
 * @param - The identifier value.
 */
export function toCoding(params: {
  system?: string;
  code?: string | null;
  display?: string;
}): Coding | undefined {
  if (!params.code) return undefined;
  return { system: params.system, code: params.code, display: params.display };
}

/**
 * Check if two Coding objects are equal by comparing code and system.
 *
 * @param a - First Coding object
 * @param b - Second Coding object
 * @returns true if both codings have the same code and system
 */
export function isEqualCoding(a: Coding, b: Coding): boolean {
  return a.code === b.code && a.system === b.system;
}

/**
 * Creates a CodeableConcept object from an array of codings.
 *
 * It filters out invalid codings.
 *
 * @param codings - The array of codings.
 * @returns The CodeableConcept object or undefined if there are no valid codings.
 */
export function toSingleCodeableConcept(
  codings: Array<{ system?: string; code?: string | null; display?: string }>,
  text?: string,
): CodeableConcept | undefined {
  const filteredCodings = codings.filter((coding) => {
    if (coding.code === undefined) return false;

    return true;
  });

  if (filteredCodings.length === 0 && !text) return undefined;
  if (filteredCodings.length === 0) return { text };

  return { text, coding: filteredCodings as CodeableConcept["coding"] };
}

/**
 * Creates a CodeableConcept object from an array of codings.
 *
 * It filters out invalid codings.
 *
 * @param codings - The array of codings.
 * @returns The CodeableConcept object or undefined if there are no valid codings.
 */
export function toCodeableConcept(
  codings: Array<{ system?: string; code?: string | null; display?: string }>,
): CodeableConcept[] | undefined {
  const codeableConcept = toSingleCodeableConcept(codings);
  if (!codeableConcept) return undefined;
  return [codeableConcept];
}

/**
 * Given a human name, returns a FHIR HumanName object.
 *
 * @param name - The human name to convert.
 * @returns The FHIR HumanName object or undefined if name is empty.
 */
export function toHumanName(name: string | null | undefined): HumanName[] | undefined {
  if (!name || name.trim() === "") {
    return undefined;
  }

  const parsedName = parseName(name);
  const parsedPrefix = parsedName.salutation?.trim();

  return [
    {
      given: [parsedName.firstName, parsedName.middleName].filter(Boolean) as string[],
      family: parsedName.lastName,
      prefix: parsedPrefix ? [parsedPrefix] : undefined,
      use: "official",
    },
  ];
}

/**
 * Converts a key-value map to an array of FHIR notes.
 *
 * Example:
 * ```typescript
 * toFhirNotes({
 *   "examReason": "The patient is experiencing a headache.",
 *   "clinicalNotes": "The patient is experiencing a headache.",
 * });
 * ```
 * @param notes - The key-value map to convert.
 * @returns The array of FHIR notes.
 */
export function toFhirNotes(
  notes: Record<string, string | undefined>,
): { id: string; text: string }[] {
  return Object.entries(notes)
    .filter(([, value]) => value !== undefined && value !== "")
    .map(([key, value]) => ({ id: key, text: value! }));
}

/**
 * Given a map of sorting options, returns a map of sorting options with ascending and descending variants.
 *
 * Example:
 * ```
 * SortingOptions({
 *   Date: "date",
 * });
 *
 * Returns:
 * {
 *   Date: {
 *     Ascending: "date",
 *     Descending: "-date",
 *   },
 * }
 * ```
 *
 * @param sortingOptions - The map of sorting options.
 * @returns The map of sorting options with ascending and descending variants.
 */
export function SortingOptions(sortingOptions: Record<string, string>) {
  const options: Record<string, { Ascending: string; Descending: string }> = {};

  for (const key in sortingOptions) {
    options[key] = { Ascending: sortingOptions[key], Descending: `-${sortingOptions[key]}` };
  }

  return options;
}

/**
 * Returns the ID from a reference string.
 *
 * @param referenceString - The reference string.
 * @returns The ID.
 */
export function getIdFromReferenceString(
  referenceString: string,
  expectedResourceType: string,
): string {
  if (!referenceString.startsWith(`${expectedResourceType}/`)) {
    throw new Error(`Invalid ${expectedResourceType} reference: ${referenceString}`);
  }
  const [_resourceType, id] = referenceString.split(`/`);
  return id;
}

/**
 * Converts an Address object to a string for display in the AddressInput.
 */
export function toStringAddress(address: Address | undefined): string {
  if (!address) return "";
  const lines = address.line?.join(", ") ?? "";
  const city = address.city ?? "";
  const state = address.state ?? "";
  const postalCode = address.postalCode ?? "";
  return [lines, city, state, postalCode].filter(Boolean).join(", ");
}

/**
 * Converts structured address data to a FHIR Address object.
 */
export function toFhirAddress(address: {
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zipCode: string;
}): Address {
  const line = [address.address1, address.address2].filter(Boolean) as string[];
  return { line, city: address.city, state: address.state, postalCode: address.zipCode };
}
